import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { calculateArea } from "../../../util/geo";
import { MoreList } from "../../components/MoreList";
import { More } from "../../components/MoreToggle";
import { TabButton } from "../../components/Tabs";
import { WozService, WozValues } from "../../services/wozService";
import { useSelected, useSelectionFor } from "../../state";
import { AddressDTO, Cube } from "../../state/types";
import { FetchState } from "../../state/util";
import { TabKey, joinBuildingsAddresses, relevantTabs } from "./buildingAddressJoin";
import { Info, Tooltip, rFormat } from "./util";

type BuildingDTO = Cube["builtUp"]["buildings"][number];

const wozFormat = new Intl.NumberFormat("NL-nl", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

const WOZPrices = ({ address }: { address: AddressDTO }) => {
  const [state, setState] = useState<FetchState>("fetching");
  const [values, setValues] = useState<WozValues>({});

  useEffect(() => {
    let active = true;
    setState("fetching");
    WozService.values(address.extId)
      .then((data) => {
        if (!active) return;
        setValues(data);
        setState("fetched");
      })
      .catch(() => active && setState("error"));
    return () => {
      active = false;
    };
  }, [address.extId]);

  if (state === "fetching")
    return (
      <div className="text-dark-gray text-sm mt-1">
        <FontAwesomeIcon icon={faSpinner} spin={true} fill="green" /> WOZ-waarden laden…
      </div>
    );
  if (state === "error")
    return <div className="text-dark-gray text-sm mt-1">Kon WOZ-waarden niet laden.</div>;

  const years = Object.keys(values)
    .map(Number)
    .sort((a, b) => b - a);
  if (!years.length)
    return <div className="text-dark-gray text-sm mt-1">Geen WOZ-waarden bekend.</div>;

  return (
    <dl className="text-sm mt-1">
      <p className="font-semibold">WOZ-waardes</p>
      {years.map((year) => (
        <div key={year} className="flex">
          <dt className="w-16 text-dark-gray">{year}</dt>
          <dd className="w-30 text-right font-mono">{wozFormat.format(values[year])}</dd>
        </div>
      ))}
    </dl>
  );
};

const addressLabel = (a: AddressDTO) =>
  `${a.street} ${a.number}, ${a.zipCode ?? ""} ${a.city}`.replace("  ", " ");

const Address = ({ address }: { address: AddressDTO }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="p-2.5">
      <More setOpen={setOpen} open={open} />
      {addressLabel(address)} ({address.area}&nbsp;m²)
      {open && <WOZPrices address={address} />}
    </div>
  );
};

const AddressesComponent = ({ data }: { data: AddressDTO[] }) => (
  <Info>
    {data.map((e) => (
      <Address key={e.extId} address={e} />
    ))}
  </Info>
);

const Addresses = ({ addresses }: { addresses: AddressDTO[] }) => (
  <div>
    {addresses.length > 1 && (
      <div className="px-2.5 text-sm">
        <span className="font-semibold">{addresses.length}</span> adressen, gebruiksoppervlakte{" "}
        <Tooltip content="Het gaat hier om de gebruiksoppervlakte wonen plus overige inpandige ruimte volgens NEN 2580 met een minimale stahoogte van 1.5 meter.">
          <span className="font-semibold">
            {rFormat.format(addresses.reduce((acc, a) => acc + (a.area ?? 0), 0))}&nbsp;m²
          </span>{" "}
          *
        </Tooltip>
      </div>
    )}
    <MoreList data={addresses} Component={AddressesComponent} size={3}>
      Toon alle {addresses.length} adressen
    </MoreList>
  </div>
);

const useBuildingSelection = () => ({
  selectedId: useSelectionFor<string>("building"),
  toggle: useSelected((s) => s.toggle),
});

const Buildings = ({ buildings }: { buildings: BuildingDTO[] }) => {
  const { selectedId, toggle } = useBuildingSelection();
  return (
    <table className="w-full table-auto">
      <thead>
        <tr>
          <th className="p-1 pb-0">BAG-id</th>
          <th className="p-1 pb-0">Opp.</th>
          <th className="p-1 pb-0">Bouw</th>
          <th className="p-1 pb-0">Status</th>
        </tr>
      </thead>
      <tbody>
        {buildings.map((b) => (
          <tr
            key={b.extId}
            className={`cursor-pointer hover:bg-bright-yellow/40 ${
              selectedId === b.extId ? "bg-bright-yellow" : ""
            }`}
            onClick={() => toggle("building", b.extId)}
          >
            <td className="px-1 font-mono" align="right">
              {b.extId}
            </td>
            <td className="px-1" align="right">
              {calculateArea(b.geometry).toFixed(0)}&nbsp;m²
            </td>
            <td className="px-1" align="right">
              {b.year}
            </td>
            <td>{b.status}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const BuildingsSummary = ({ buildings }: { buildings: BuildingDTO[] }) => {
  const years = buildings.map((b) => b.year);
  const [minYear, maxYear] = [Math.min(...years), Math.max(...years)];
  const status = [...new Set(buildings.map((b) => b.status))];
  return (
    <div className="px-2.5 text-sm">
      Bouwjaar{" "}
      <span className="font-semibold">
        {minYear === maxYear ? minYear : `${minYear} - ${maxYear}`}
      </span>
      ; {status.join(", ")}
    </div>
  );
};

const BuildingLine = ({ building }: { building: BuildingDTO }) => {
  const { selectedId, toggle } = useBuildingSelection();
  return (
    <div
      className={`cursor-pointer hover:bg-bright-yellow/40 ${
        selectedId === building.extId ? "bg-bright-yellow" : ""
      }`}
      onClick={() => toggle("building", building.extId)}
    >
      <span className="font-mono">{building.extId}</span> - {building.year}
      {" - "}
      {calculateArea(building.geometry).toFixed(0)}&nbsp;m² - {building.status}
    </div>
  );
};

const AddressesPerBuilding = ({
  buildings,
  addressesPerBuilding,
}: {
  buildings: BuildingDTO[];
  addressesPerBuilding: Map<string, AddressDTO[]>;
}) => (
  <Info>
    {buildings.map((b) => {
      const linked = addressesPerBuilding.get(b.extId) ?? [];
      return (
        <div key={b.extId} className="p-2.5">
          <BuildingLine building={b} />
          <div className="pl-4 text-dark-gray">
            {linked.length
              ? linked.map((a) => (
                  <div key={a.extId}>
                    {addressLabel(a)} {a.area ? `(${a.area} m²)` : ""}
                  </div>
                ))
              : "— geen adres"}
          </div>
        </div>
      );
    })}
  </Info>
);

const BuildingsPerAddress = ({
  addresses,
  buildingsPerAddress,
}: {
  addresses: AddressDTO[];
  buildingsPerAddress: Map<string, BuildingDTO[]>;
}) => (
  <Info>
    {addresses.map((a) => {
      const linked = buildingsPerAddress.get(a.extId) ?? [];
      return (
        <div key={a.extId} className="p-2.5">
          <div className="font-semibold">{addressLabel(a)}</div>
          <div className="pl-4">
            {linked.length ? (
              linked.map((b) => <BuildingLine key={b.extId} building={b} />)
            ) : (
              <span className="text-dark-gray">— geen gebouw</span>
            )}
          </div>
        </div>
      );
    })}
  </Info>
);

const labels: Record<TabKey, string> = {
  addresses: "Adressen",
  buildings: "Gebouwen",
  perBuilding: "per gebouw",
  perAddress: "per adres",
};

export const BuildingsAddresses = ({
  buildings,
  addresses,
}: {
  buildings: BuildingDTO[];
  addresses: AddressDTO[];
}) => {
  const join = useMemo(() => joinBuildingsAddresses(buildings, addresses), [buildings, addresses]);
  const tabs = relevantTabs(buildings, addresses, join);
  const [tab, setTab] = useState<TabKey>(tabs[0]);
  if (!tabs.length) return null;
  const active = tabs.includes(tab) ? tab : tabs[0];

  const counts: Partial<Record<TabKey, number>> = {
    addresses: addresses.length,
    buildings: buildings.length,
  };
  return (
    <div className="text-sm">
      {tabs.length > 1 ? (
        <div className="flex flex-wrap px-1">
          {tabs.map((t) => (
            <TabButton key={t} className="text-sm" active={active === t} onClick={() => setTab(t)}>
              {labels[t]}
              {counts[t] != null && (
                <span className="text-gray-400 font-normal"> ({counts[t]})</span>
              )}
            </TabButton>
          ))}
        </div>
      ) : (
        <div className="px-2.5 text-sm font-semibold">{labels[active]}</div>
      )}
      {active === "addresses" && <Addresses addresses={addresses} />}
      {active === "buildings" && (
        <div>
          <BuildingsSummary buildings={buildings} />
          <Buildings buildings={buildings} />
        </div>
      )}
      {active === "perBuilding" && (
        <AddressesPerBuilding
          buildings={buildings}
          addressesPerBuilding={join.addressesPerBuilding}
        />
      )}
      {active === "perAddress" && (
        <BuildingsPerAddress addresses={addresses} buildingsPerAddress={join.buildingsPerAddress} />
      )}
    </div>
  );
};
